import React, { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import { AppStrings, formatNumber, logApiErrorToSentry } from "../../utils";
import { NEFT, NetBanking } from "../../../../assets/Icons";
import { styles } from "./styles";
import { NeftRtgsModal, SendPaymentLinkModal } from "../../components/modal";
import { remittanceActions } from "../../state/remittanceSlice";
import { remittanceApi } from "../../api/remittanceApi";
import MixpanelConfig from "../../../../constants/MixpanelConfig";

declare global {
  interface Window {
    Cashfree?: any;
    Razorpay?: any;
  }
}

const RemittancePayment: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const authState = useAppSelector((state) => state.auth);
  const remitterState = useAppSelector((state) => state.remittance);
  const { transactionDetail, payment, remitter } = useAppSelector((state) => state.remittance);
  const [cashfreeBackdrop, setCashfreeBackdrop] = useState(false);

  const resetNetBankingInput = () => {
    dispatch(
      remittanceActions.setSendPaymentLinkInput({
        pennyDropLoading: false,
        requestRazorLoading: false,
        bankDropdown: null,
        accountNumber: "",
        IFSCcode: "",
      })
    );
  };

  {
    /* Initial API call and condition when user came to this page */
  }
  useEffect(() => {
    // dispatch(remittanceActions.fetchGetNeftRtgsBankDetailsRequested({
    //   ad1Transaction: transactionDetail?.data?.purposeCode === "S0305" ? false : true,
    //   referrerVendorCode: transactionDetail?.data?.referrerVendor
    // }));
    MixpanelConfig.trackEvent(MixpanelConfig.eventNames.New_Payment_Landing, {
      authState: authState,
      remitterState: remitterState,
      transactionDetail: transactionDetail,
    });

    dispatch(remittanceActions.fetchNetBankingListRequested());
  }, []);

  useEffect(() => {
    if (payment?.neftRtgsBankDetails?.error) {
      toast.error(payment?.neftRtgsBankDetails?.error);
    }
    if (payment?.netBankingList?.error) {
      toast.error(payment?.netBankingList?.error);
    }
  }, [payment?.neftRtgsBankDetails?.error, payment?.netBankingList?.error]);

  useEffect(() => {
    if (remitterState?.progresStep?.activeStepNumber < 6) {
      navigate("/send/calculator", { replace: true });
    }
  }, [remitterState?.progresStep?.activeStepNumber]);

  useEffect(() => {
    const handleBeforeUnload = (event: any) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  const sendPaymentLink = () => {
    const pennyDropPayload = {
      cusAcc: payment?.sendPaymentLinkInput?.accountNumber,
      ifscCode: payment?.sendPaymentLinkInput?.IFSCcode,
      isVpa: false,
      transactionID: transactionDetail?.data?.id_transaction,
      userId: 0,
      userKey: transactionDetail?.data?.remitterId,
      vpa: "",
    };
    const requestRazorLinkPayload = {
      accNum: payment?.sendPaymentLinkInput?.accountNumber,
      ifsc: payment?.sendPaymentLinkInput?.IFSCcode,
      paymentMode: "NET_BANKING",
      transactionID: transactionDetail?.data?.id_transaction,
      userId: 0,
      userKey: transactionDetail?.data?.remitterId,
    };
    dispatch(remittanceActions.setSendPaymentLinkInput({ pennyDropLoading: true }));
    remittanceApi
      .postPennyDrop(pennyDropPayload)
      .then((response: any) => {
        if (response?.data?.success) {
          dispatch(
            remittanceActions.setSendPaymentLinkInput({
              pennyDropLoading: false,
              requestRazorLoading: true,
            })
          );
          remittanceApi
            .postRequestRazorPayLink(requestRazorLinkPayload)
            .then((response: any) => {
              if (response?.data?.success) {
                toast.success("Link sent successfully to remitter's mobile number");
                dispatch(
                  remittanceActions.setSendPaymentLinkInput({
                    pennyDropLoading: false,
                    requestRazorLoading: false,
                    bankDropdown: null,
                    accountNumber: "",
                    IFSCcode: "",
                  })
                );
                dispatch(remittanceActions.setModalType(""));
                navigate("/send/confirmation");
              } else {
                dispatch(
                  remittanceActions.setSendPaymentLinkInput({
                    pennyDropLoading: false,
                    requestRazorLoading: false,
                  })
                );
                toast.error(AppStrings.GENERIC_ERROR);
              }
            })
            .catch((error: any) => {
              logApiErrorToSentry(error, "sendPaymentLink postRequestRazorPayLink");
              toast.error(
                error?.response?.data?.error?.type ||
                  error?.response?.error ||
                  error?.message ||
                  AppStrings.GENERIC_ERROR
              );
              dispatch(
                remittanceActions.setSendPaymentLinkInput({
                  pennyDropLoading: false,
                  requestRazorLoading: false,
                })
              );
            });
        } else {
          dispatch(
            remittanceActions.setSendPaymentLinkInput({
              pennyDropLoading: false,
              requestRazorLoading: false,
            })
          );
          toast.error(AppStrings.GENERIC_ERROR);
        }
      })
      .catch((error: any) => {
        logApiErrorToSentry(error, "sendPaymentLink postPennyDrop");
        toast.error(
          error?.response?.data?.error?.type ||
            error?.response?.error ||
            error?.message ||
            AppStrings.GENERIC_ERROR
        );
        dispatch(
          remittanceActions.setSendPaymentLinkInput({
            pennyDropLoading: false,
            requestRazorLoading: false,
          })
        );
      });
  };

  const paymentErrorMessage = (error: any) =>
    error?.response?.data?.error?.type ||
    error?.response?.error ||
    error?.message ||
    AppStrings.GENERIC_ERROR;

  const handleRazorSuccess = (response: any) => {
    if (response.status === 200) {
      resetNetBankingInput();
      dispatch(remittanceActions.setModalType(""));
      if (response.data?.redirectUrl) {
        localStorage.setItem("matchingUrl", response.data.redirectUrl);
      }
      window.location.href = "/send/confirmation";
    }
  };

  const handleNetBanking = () => {
    const userKey = transactionDetail?.data?.remitterId;
    const pennyDropPayload = {
      cusAcc: payment?.sendPaymentLinkInput?.accountNumber,
      ifscCode: payment?.sendPaymentLinkInput?.IFSCcode,
      isVpa: false,
      transactionID: transactionDetail?.data?.id_transaction,
      userId: 0,
      userKey,
      vpa: "",
    };
    const requestRazorPayload = {
      accNum: payment?.sendPaymentLinkInput?.accountNumber,
      ifsc: payment?.sendPaymentLinkInput?.IFSCcode,
      paymentMode: "NET_BANKING",
      transactionID: transactionDetail?.data?.id_transaction,
      userId: 0,
      userKey,
    };

    dispatch(remittanceActions.setSendPaymentLinkInput({ pennyDropLoading: true }));
    remittanceApi
      .postPennyDrop(pennyDropPayload)
      .then((response: any) => {
        if (!response?.data?.success) {
          dispatch(
            remittanceActions.setSendPaymentLinkInput({
              pennyDropLoading: false,
              requestRazorLoading: false,
            })
          );
          toast.error(AppStrings.GENERIC_ERROR);
          return;
        }

        dispatch(
          remittanceActions.setSendPaymentLinkInput({
            pennyDropLoading: false,
            requestRazorLoading: true,
          })
        );

        remittanceApi
          .postRequestRazor(requestRazorPayload)
          .then((razorResponse: any) => {
            dispatch(
              remittanceActions.setSendPaymentLinkInput({
                pennyDropLoading: false,
                requestRazorLoading: false,
              })
            );
            dispatch(remittanceActions.setModalType(""));

            if (razorResponse?.data?.openRazorpay === false) {
              const dropConfig = {
                components: ["netbanking"],
                orderToken: razorResponse?.data?.order,
                onSuccess: function (data: any) {
                  remittanceApi
                    .postRazorPayResponse({
                      razorpay_order_id: data.order.orderId,
                      razorpay_payment_id: data.transaction.transactionId,
                      isRazorPay: false,
                      razorpay_signature: "",
                    })
                    .then(handleRazorSuccess)
                    .catch((error: any) => {
                      setCashfreeBackdrop(false);
                      toast.error(paymentErrorMessage(error));
                    });
                },
                onFailure: () => {
                  setCashfreeBackdrop(false);
                },
                style: {
                  backgroundColor: "#ffffff",
                  color: "#11385b",
                  fontFamily: "Lato",
                  fontSize: "14px",
                  errorColor: "#ff0000",
                  theme: "light",
                },
              };
              const cashfree: any = new window.Cashfree();
              const paymentElement: any = document.getElementById("payment-form");
              if (paymentElement?.firstChild) paymentElement.firstChild.remove();
              cashfree.initialiseDropin(paymentElement, dropConfig);
              setCashfreeBackdrop(true);
              return;
            }

            const options = {
              key: razorResponse?.data.key,
              amount: razorResponse?.data.amount,
              name: "K2 Remit",
              description: "Remit Transaction",
              order_id: razorResponse?.data.orderID,
              handler: async function (checkoutResponse: any) {
                remittanceApi
                  .postRazorPayResponse({
                    razorpay_payment_id: checkoutResponse.razorpay_payment_id,
                    razorpay_order_id: checkoutResponse.razorpay_order_id,
                    razorpay_signature: checkoutResponse.razorpay_signature,
                    isRazorPay: true,
                  })
                  .then(handleRazorSuccess)
                  .catch((error: any) => {
                    toast.error(paymentErrorMessage(error));
                  });
              },
              modal: {
                ondismiss: function () {
                  window.location.reload();
                },
              },
              prefill: {
                name: remitter?.selectedRemitter?.firstName || "",
                email: remitter?.selectedRemitter?.email || authState?.userEmail || "",
              },
              notes: {
                address: "MoneyHop",
              },
              theme: {
                color: "#3B74FA",
              },
              method: {
                netbanking: true,
              },
            };

            const rzp = new window.Razorpay(options);
            rzp.open();
          })
          .catch((error: any) => {
            logApiErrorToSentry(error, "handleNetBanking postRequestRazor");
            toast.error(paymentErrorMessage(error));
            dispatch(
              remittanceActions.setSendPaymentLinkInput({
                pennyDropLoading: false,
                requestRazorLoading: false,
              })
            );
          });
      })
      .catch((error: any) => {
        logApiErrorToSentry(error, "handleNetBanking postPennyDrop");
        toast.error(paymentErrorMessage(error));
        dispatch(
          remittanceActions.setSendPaymentLinkInput({
            pennyDropLoading: false,
            requestRazorLoading: false,
          })
        );
      });
  };

  return (
    <>
      <div className={styles.container}>
        <ToastContainer autoClose={3000} position="top-right" />

        {/* Header */}
        <div id="header" className={styles.headerWrapper}>
          <span className={styles.transactionIdText}>
            {AppStrings.PAYMENT_SCREEN.TRANSACTION_ID_LABEL}{" "}
            {transactionDetail?.data?.transactionNumber}
          </span>

          <span className={styles.payableAmountText}>
            {AppStrings.PAYMENT_SCREEN.PAYABLE_AMOUNT_LABEL}{" "}
            {formatNumber(transactionDetail?.data?.total_pay)} {AppStrings.PAYMENT_SCREEN.CURRENCY}
          </span>

          <span className={styles.subtitleText}>
            {AppStrings.PAYMENT_SCREEN.SELECT_PAYMENT_METHOD}
          </span>
        </div>

        {/* Payment Options */}
        <div id="boxes" className={styles.paymentOptionsWrapper}>
          <div
            id="neft"
            onClick={() => {
              dispatch(remittanceActions.setModalType("NEFT_RTGS"));
              dispatch(
                remittanceActions.fetchGetNeftRtgsBankDetailsRequested({
                  ad1Transaction: transactionDetail?.data?.purposeCode === "S0305" ? false : true,
                  referrerVendorCode: transactionDetail?.data?.referrerVendor,
                })
              );
            }}
            className={styles.payButton}
          >
            <NEFT />
            <span className={styles.paymentLabelText}>{AppStrings.PAYMENT_SCREEN.NEFT_RTG}</span>
          </div>

          {/* <div
            id="netBanking"
            onClick={() => {
              dispatch(remittanceActions.setModalType("NET_BANKING"));
            }}
            className={styles.payButton}
          >
            <NetBanking />
            <span className={styles.paymentLabelText}>{AppStrings.PAYMENT_SCREEN.NET_BANKING}</span>
          </div> */}
          {/* <div
            id="link"
            onClick={() => {
              dispatch(remittanceActions.setModalType("SEND_PAYMENT_LINK"));
            }}
            className={styles.payButton}
          >
            <DebitCard />
            <span className={styles.paymentLabelCentered}>
              {AppStrings.PAYMENT_SCREEN.SEND_PAYMENT_LINK}
            </span>
          </div> */}
        </div>
      </div>
      <NeftRtgsModal
        visible={remitterState?.modalType === "NEFT_RTGS"}
        // onYes={() => {
        //   dispatch(remittanceActions.setModalType(""));
        //   dispatch(remittanceActions.setAmountCalculation({ isEducation: true }));
        // }}
        // onNo={() => {
        //   dispatch(remittanceActions.setModalType(""));
        //   dispatch(remittanceActions.setAmountCalculation({ isEducation: false }));
        // }}
      />
      <SendPaymentLinkModal
        visible={
          remitterState?.modalType === "NET_BANKING" ||
          remitterState?.modalType === "SEND_PAYMENT_LINK"
        }
        header={
          remitterState?.modalType === "NET_BANKING"
            ? AppStrings.PAYMENT_SCREEN.NET_BANKING
            : "Generate Payment Link"
        }
        submitLabel={remitterState?.modalType === "NET_BANKING" ? "Confirm" : "Submit"}
        onSubmit={remitterState?.modalType === "NET_BANKING" ? handleNetBanking : sendPaymentLink}
        onClose={() => {
          resetNetBankingInput();
          dispatch(remittanceActions.setModalType(""));
        }}
      />
      <div
        style={{
          position: "fixed",
          left: "0px",
          top: "0px",
          height: "100vh",
          width: "100vw",
          backgroundColor: "rgba(0,0,0,0.5)",
          display: cashfreeBackdrop ? "block" : "none",
          zIndex: 50,
        }}
      >
        <div
          style={{
            position: "fixed",
            left: "50%",
            top: "50%",
            width: "500px",
            height: "500px",
            transform: "translate(-50%, -50%)",
          }}
        >
          <div id="payment-form" style={{ height: "500px", width: "500px" }}></div>
        </div>
      </div>
    </>
  );
};

export default RemittancePayment;
